<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { RouterLink } from 'vue-router';

const { proxy } = getCurrentInstance();
const { $stores } = proxy;

// Accéder au store des produits via le proxy
const productsStore = $stores?.products;

const selectedCategory = ref('all');
const loading = ref(true);

const categories = [
  { id: 'all', name: 'Tout', icon: 'grid_view', color: '#7C3AED' },
  { id: 'giftcard', name: 'Cartes Cadeaux', icon: 'card_giftcard', color: '#D4AF37' },
  { id: 'subscription', name: 'Abonnements', icon: 'subscriptions', color: '#FF385C' },
];

// Charger les produits au montage du composant
onMounted(async () => {
  try {
    // Si le store n'est pas encore chargé, forcer le chargement
    if (!productsStore?.state?.allProducts?.length) {
      await productsStore?.fetchProducts();
    }
  } catch (error) {
    console.error('Erreur chargement produits:', error);
  } finally {
    loading.value = false;
  }
});

// Tous les produits depuis le store
const products = computed(() => productsStore?.state?.allProducts || []);

// Produits filtrés par catégorie
const filteredProducts = computed(() => {
  if (selectedCategory.value === 'all') return products.value;
  return productsStore?.filterByCategory(selectedCategory.value) || [];
});

const currentPage = ref(1);
const itemsPerPage = 10;
const totalPages = computed(() => Math.ceil(filteredProducts.value.length / itemsPerPage));
const paginatedProducts = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  return filteredProducts.value.slice(start, start + itemsPerPage);
});
</script>

<template>
  <div class="min-h-screen" style="background: #0A0A0B;">
    <div class="max-w-7xl mx-auto px-6 lg:px-8 py-12">

      <!-- Page Header -->
      <div class="mb-12">
        <h1 class="font-black text-4xl md:text-5xl mb-4 text-white" style="font-family: 'Outfit', sans-serif;">
          <span style="background: linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">Boutique</span>
        </h1>
        <p class="text-white/60 text-lg">Découvrez tous nos produits digitaux premium</p>
      </div>

      <!-- Breadcrumb -->
      <nav aria-label="Breadcrumb" class="flex items-center text-sm text-white/50 mb-10">
        <RouterLink class="hover:text-white transition-colors" to="/">Accueil</RouterLink>
        <span class="mx-2">/</span>
        <span class="text-white">Boutique</span>
      </nav>

      <!-- Categories Grid -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
        <button
          v-for="category in categories"
          :key="category.id"
          @click="selectedCategory = category.id"
          class="category-card group relative overflow-hidden aspect-[3/1] flex items-center justify-start px-6 font-bold text-white rounded-xl transition-all duration-300 hover:scale-[1.02]"
          :class="selectedCategory === category.id ? 'ring-2' : ''"
          :style="{
            background: `linear-gradient(135deg, ${category.color}40 0%, ${category.color}00 70%), #1A1A1D`,
            ringColor: selectedCategory === category.id ? category.color : 'transparent'
          }"
        >
          <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style="background: radial-gradient(circle at 30% 50%, rgba(255,255,255,0.1) 0%, transparent 50%);"></div>
          <span class="material-icons-outlined mr-3 text-2xl" :style="{ color: category.color }">{{ category.icon }}</span>
          <span class="relative z-10 text-base md:text-lg tracking-wide" style="font-family: 'Outfit', sans-serif;">{{ category.name }}</span>
        </button>
      </div>

      <!-- Products Section -->
      <section>
        <div class="flex items-center justify-between mb-8 pb-4" style="border-bottom: 1px solid rgba(255, 255, 255, 0.06);">
          <h2 class="text-2xl font-bold text-white" style="font-family: 'Outfit', sans-serif;">
            {{ filteredProducts.length }} produit{{ filteredProducts.length > 1 ? 's' : '' }}
          </h2>
          <div class="flex items-center gap-2 text-sm text-white/50">
            <span>Affichage {{ (currentPage - 1) * itemsPerPage + 1 }}–{{ Math.min(currentPage * itemsPerPage, filteredProducts.length) }}</span>
          </div>
        </div>

        <div v-if="loading" class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
          <div v-for="i in 10" :key="i" class="aspect-square rounded-2xl animate-pulse" style="background: rgba(255, 255, 255, 0.03);"></div>
        </div>

        <div v-else class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
          <RouterLink
            v-for="product in paginatedProducts"
            :key="product.id"
            :to="`/produit/${product.id}`"
            class="group cursor-pointer"
          >
            <div class="aspect-square rounded-2xl overflow-hidden mb-4 transition-all duration-300 transform group-hover:-translate-y-2 group-hover:shadow-2xl flex items-center justify-center relative" style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06);">
              <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style="background: radial-gradient(circle at 50% 50%, rgba(124, 58, 237, 0.15) 0%, transparent 70%);"></div>
              <img v-if="product.image" :alt="product.name" class="w-full h-full object-contain p-6 relative z-10" :src="product.image"/>
              <span v-else class="material-icons-outlined text-4xl text-white/20 relative z-10">subscriptions</span>
            </div>
            <h3 class="font-semibold text-white group-hover:text-white/80 transition-colors" style="font-family: 'Outfit', sans-serif;">{{ product.name || product.title }}</h3>
            <p class="font-bold mt-1" style="color: #7C3AED;">{{ product.priceDisplay || `${product.price} CFA` }}</p>
          </RouterLink>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="mt-16 flex justify-center">
          <nav class="flex items-center gap-2">
            <button
              v-for="page in totalPages"
              :key="page"
              @click="currentPage = page"
              class="w-12 h-12 flex items-center justify-center rounded-xl font-bold transition-all duration-300"
              :class="currentPage === page ? 'text-white shadow-lg' : 'text-white/60 hover:bg-white/5'"
              :style="currentPage === page ? 'background: linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%); box-shadow: 0 4px 15px rgba(124, 58, 237, 0.3);' : ''"
            >
              {{ page }}
            </button>
          </nav>
        </div>
      </section>

    </div>
  </div>
</template>

<style scoped>
.category-card {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}
</style>
